import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Bar, Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import SegmentChartCardKebabMenu from "./SegmentChartCardKebabMenu";
import { DashboardChartTableSortHeader } from "./DashboardChartTableSortHeader";
import { mergePaymentMethodModel } from "./dashboardChartsModel";
import { buildTimestampedPngFilename, downloadElementAsPng } from "./downloadCapturePng";
import "./dashboardChartJsSetup";

function formatAmt(n) {
  const v = typeof n === "number" ? n : Number(n);
  if (!Number.isFinite(v)) return "";
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 }).format(Math.round(v));
}

function formatPct(p) {
  if (!Number.isFinite(p)) return "0";
  const rounded = Math.abs(p - Math.round(p)) < 1e-6 ? Math.round(p) : Number(p.toFixed(1));
  return String(rounded);
}

/**
 * @param {{ refundAmount?: number; className?: string; t: (k: string, o?: object) => string }} props
 */
function PaymentMethodRefundRow({ refundAmount, className = "", t }) {
  const n = Number(refundAmount);
  const amt = Number.isFinite(n) ? n : 0;
  const label = t("refundCash", { defaultValue: "Refund cash" });
  return (
    <div
      className={`pos-payment-method-refund${className ? ` ${className}` : ""}`.trim()}
      role="group"
      aria-label={label}
    >
      <span className="pos-payment-method-refund__label">{label}</span>
      <span className="pos-payment-method-refund__value">{formatAmt(amt)}</span>
    </div>
  );
}

/**
 * @param {(key: string, opts?: object) => string} t
 * @param {import("./dashboardChartsModel.js").PaymentMethodItem} item
 */
function itemDisplayLabel(t, item) {
  const key = item.labelKey;
  if (key) return t(key, { defaultValue: item.label });
  return item.label;
}

/**
 * @param {import("./dashboardChartsModel.js").PaymentMethodModel} model
 */
function deriveRows(model) {
  const items = model?.items ?? [];
  const total =
    typeof model?.totalSale === "number" && Number.isFinite(model.totalSale)
      ? model.totalSale
      : items.reduce((s, x) => s + (Number(x.amount) || 0), 0);
  return {
    total,
    rows: items.map((it) => {
      const amt = Number(it.amount) || 0;
      const pct = total > 0 ? (amt / total) * 100 : 0;
      return { ...it, amount: amt, pct };
    }),
  };
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").PaymentMethodModel }} props
 */
function PaymentMethodDonutBody({ model }) {
  const { t } = useTranslation();
  const { rows, total } = useMemo(() => deriveRows(model), [model]);

  const chartData = useMemo(
    () => ({
      labels: rows.map((r) => itemDisplayLabel(t, r)),
      datasets: [
        {
          data: rows.map((r) => r.amount),
          backgroundColor: rows.map((r) => r.color),
          borderColor: "#FFFFFF",
          borderWidth: 1,
        },
      ],
    }),
    [rows, t],
  );

  const options = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "82%",
      interaction: { mode: "nearest", intersect: true },
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            title: () => "",
            label: (ctx) => {
              const i = ctx.dataIndex;
              const name = itemDisplayLabel(t, rows[i]);
              const amt = rows[i]?.amount ?? 0;
              const pct = total > 0 ? ((amt / total) * 100).toFixed(1) : "0";
              return [`${name}: ${formatAmt(amt)}`, `(${pct}%)`];
            },
          },
        },
      },
    }),
    [rows, t, total],
  );

  return (
    <div className="pos-payment-method-donut" aria-label={t("graphPaymentMethod", { defaultValue: "Payment Method" })}>
      <div className="pos-payment-method-donut__legend" role="list">
        {rows.map((r, i) => (
          <div key={r.id || i} className="pos-payment-method-donut__legend-row" role="listitem">
            <div className="pos-payment-method-donut__legend-name">
              <span className="pos-payment-method-donut__dot" style={{ background: r.color }} aria-hidden />
              <span>{itemDisplayLabel(t, r)}</span>
            </div>
            <span className="pos-payment-method-donut__legend-val">{formatAmt(r.amount)}</span>
            <span className="pos-payment-method-donut__legend-pct" style={{fontSize:'10px',color:'#A4A4A4'}}>({formatPct(r.pct)}%)</span>
          </div>
        ))}
        <PaymentMethodRefundRow refundAmount={model?.refundAmount} t={t} className="pos-payment-method-donut__refund" />
      </div>
      <div className="pos-payment-method-donut__chart">
        <div className="pos-payment-method-donut__canvas-wrap">
          <div className="pos-payment-method-donut__canvas">
            <Doughnut data={chartData} options={options} />
          </div>
          <div className="pos-payment-method-donut__center" aria-hidden>
            <span className="pos-payment-method-donut__center-label">{t("totalSale", { defaultValue: "Total Sale" })}</span>
            <span className="pos-payment-method-donut__center-value">{formatAmt(total)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").PaymentMethodModel }} props
 */
function PaymentMethodBarBody({ model }) {
  const { t } = useTranslation();
  const { rows, total } = useMemo(() => deriveRows(model), [model]);
  const maxAmt = useMemo(() => Math.max(0, ...rows.map((r) => r.amount)), [rows]);
  const yMax = useMemo(() => {
    const cap = Math.max(maxAmt * 1.2, 750000);
    return Math.ceil(cap / 250000) * 250000;
  }, [maxAmt]);

  const { chartData, options } = useMemo(() => {
    const labels = rows.map((r) => itemDisplayLabel(t, r));
    const data = {
      labels,
      datasets: [
        {
          data: rows.map((r) => r.amount),
          backgroundColor: rows.map((r) => r.color),
          borderRadius: 0,
          borderSkipped: false,
          maxBarThickness: 44,
        },
      ],
    };
    const opts = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const v = ctx.parsed.y;
              return formatAmt(v);
            },
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
          },
        },
        y: {
          suggestedMax: yMax,
          ticks: {
            stepSize: 250000,
            color: "#666666",
            font: { family: "Roboto, sans-serif", size: 11 },
            callback: (v) => (Math.abs(Number(v)) >= 1000 ? `${Math.round(Number(v) / 1000)}k` : String(v)),
          },
          grid: {
            color: "#E6E6E6",
            drawTicks: false,
          },
          border: { display: false },
        },
      },
    };
    return { chartData: data, options: opts };
  }, [rows, t, yMax]);

  return (
    <div className="pos-payment-method-bar" aria-label={t("graphPaymentMethod", { defaultValue: "Payment Method" })}>
      <div className="pos-payment-method-bar__summary">
        <span className="pos-payment-method-bar__summary-label">{t("totalSalesCapital", { defaultValue: "Total Sales" })}</span>
        <span className="pos-payment-method-bar__summary-value">{formatAmt(total)}</span>
      </div>
      <div className="pos-payment-method-bar__chart">
        <Bar data={chartData} options={options} />
      </div>
      <div className="pos-payment-method-bar__legend" role="list">
        {rows.map((r, i) => (
          <div key={r.id || i} className="pos-payment-method-bar__legend-item" role="listitem">
            <span className="pos-payment-method-donut__dot pos-payment-method-bar__legend-dot" style={{ background: r.color }} aria-hidden />
            <span>{itemDisplayLabel(t, r)}</span>
          </div>
        ))}
      </div>
      <PaymentMethodRefundRow refundAmount={model?.refundAmount} t={t} className="pos-payment-method-bar__refund" />
    </div>
  );
}

/**
 * @param {{ model: import("./dashboardChartsModel.js").PaymentMethodModel }} props
 */
function PaymentMethodTableBody({ model }) {
  const { t } = useTranslation();
  const { rows: derived } = useMemo(() => deriveRows(model), [model]);

  const [sort, setSort] = useState(() => ({
    key: /** @type {"name"|"amount"|"pct"} */ ("amount"),
    dir: /** @type {"asc"|"desc"} */ ("desc"),
  }));

  useEffect(() => {
    setSort({ key: "amount", dir: "desc" });
  }, [model]);

  const toggleSort = useCallback((key) => {
    setSort((s) => (s.key !== key ? { key, dir: "asc" } : { key, dir: s.dir === "asc" ? "desc" : "asc" }));
  }, []);

  const rows = useMemo(() => {
    const copy = derived.map((r, i) => ({ r, i, name: itemDisplayLabel(t, r) }));
    const mul = sort.dir === "asc" ? 1 : -1;
    copy.sort((a, b) => {
      let c = 0;
      switch (sort.key) {
        case "name":
          c = a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
          break;
        case "amount":
          c = a.r.amount - b.r.amount;
          break;
        case "pct":
          c = a.r.pct - b.r.pct;
          break;
        default:
          c = 0;
      }
      if (c !== 0) return c * mul;
      return a.i - b.i;
    });
    return copy.map((x) => x.r);
  }, [derived, sort, t]);

  const thBase = "pos-payment-method-table__th";
  const thSorted = "pos-payment-method-table__th--sorted";

  return (
    <div className="pos-payment-method-table" role="table" aria-label={t("graphPaymentMethod", { defaultValue: "Payment Method" })}>
      <div className="pos-payment-method-table__head" role="rowgroup">
        <div className="pos-payment-method-table__tr pos-payment-method-table__tr--head" role="row">
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-payment-method-table__th--name"
            ariaSortLabel={t("Sort by payment method", { defaultValue: "Sort by payment method" })}
            sorted={sort.key === "name"}
            direction={sort.dir}
            onSort={() => toggleSort("name")}
          >
            {t("paymentMethodColumn", { defaultValue: "Payment Method" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-payment-method-table__th--num"
            ariaSortLabel={t("Sort by sales", { defaultValue: "Sort by sales" })}
            sorted={sort.key === "amount"}
            direction={sort.dir}
            onSort={() => toggleSort("amount")}
          >
            {t("Sales", { defaultValue: "Sales" })}
          </DashboardChartTableSortHeader>
          <DashboardChartTableSortHeader
            baseClassName={thBase}
            sortedClassName={thSorted}
            className="pos-payment-method-table__th--pct"
            ariaSortLabel={t("Sort by percent", { defaultValue: "Sort by percent" })}
            sorted={sort.key === "pct"}
            direction={sort.dir}
            onSort={() => toggleSort("pct")}
          >
            {t("percentSign", { defaultValue: "%" })}
          </DashboardChartTableSortHeader>
        </div>
      </div>
      <div className="pos-payment-method-table__scroll" role="rowgroup">
        {rows.map((r, i) => (
          <div key={r.id || i} className="pos-payment-method-table__tr" role="row">
            <div className="pos-payment-method-table__td pos-payment-method-table__td--name" role="cell">
              {itemDisplayLabel(t, r)}
            </div>
            <div className="pos-payment-method-table__td pos-payment-method-table__td--num" role="cell">
              {formatAmt(r.amount)}
            </div>
            <div className="pos-payment-method-table__td pos-payment-method-table__td--pct" role="cell">
              {formatPct(r.pct)}%
            </div>
          </div>
        ))}
      </div>
      <div className="pos-payment-method-table__foot" role="rowgroup">
        <div className="pos-payment-method-table__tr pos-payment-method-table__tr--foot" role="row">
          <div className="pos-payment-method-table__td pos-payment-method-table__td--name" role="cell">
            {t("refundCash", { defaultValue: "Refund cash" })}
          </div>
          <div className="pos-payment-method-table__td pos-payment-method-table__td--num" role="cell">
            {formatAmt(Number.isFinite(Number(model?.refundAmount)) ? Number(model.refundAmount) : 0)}
          </div>
          <div className="pos-payment-method-table__td pos-payment-method-table__td--pct" role="cell">
            —
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Payment method — donut, bar, table; kebab Pie Chart / Chart / Table / Download / Remove.
 *
 * @param {object} props
 * @param {import("./dashboardChartsModel.js").PaymentMethodModel | undefined} [props.model]
 * @param {() => void} [props.onMenuRemove]
 */
function PaymentMethodChartCard({ model, onMenuRemove }) {
  const { t } = useTranslation();
  const captureRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const [viewKind, setViewKind] = useState(/** @type {"donut"|"bar"|"table"} */ ("donut"));
  const merged = useMemo(() => mergePaymentMethodModel(model ?? null), [model]);

  const handleDownloadPng = useCallback(async () => {
    await downloadElementAsPng(captureRef.current, buildTimestampedPngFilename("payment-method"));
  }, []);

  return (
    <div
      className={`pos-dashboard-chart-card pos-dashboard-chart-card--payment-method${
        viewKind === "table" ? " pos-dashboard-chart-card--payment-method-table" : ""
      }`}
    >
      <header className="pos-dashboard-chart-card__header">
        <h2 className="pos-dashboard-chart-card__title">{t("graphPaymentMethod", { defaultValue: "Payment Method" })}</h2>
        <SegmentChartCardKebabMenu
          viewKind={viewKind}
          onViewKindChange={setViewKind}
          onDownload={handleDownloadPng}
          onRemove={onMenuRemove}
        />
      </header>
      <div
        className={`pos-dashboard-chart-card__body pos-dashboard-chart-card__body--payment-method${
          viewKind === "table" ? " pos-dashboard-chart-card__body--payment-method-table" : ""
        }`}
      >
        <div ref={captureRef} className="pos-dashboard-chart-card__capture pos-dashboard-chart-card__capture--payment-method">
          {viewKind === "donut" ? <PaymentMethodDonutBody model={merged} /> : null}
          {viewKind === "bar" ? <PaymentMethodBarBody model={merged} /> : null}
          {viewKind === "table" ? <PaymentMethodTableBody model={merged} /> : null}
        </div>
      </div>
    </div>
  );
}

export default PaymentMethodChartCard;
